import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Search, RefreshCw, Calendar as CalendarIcon, X, Clock, CheckCircle, 
  Car, XCircle, Eye, Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const STATUS_CONFIG = {
  PENDING: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  ACCEPTED: { label: 'Aceito', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  IN_PROGRESS: { label: 'Em Andamento', color: 'bg-purple-100 text-purple-700', icon: Car },
  COMPLETED: { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  PAID: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  CANCELLED: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle }
};

export default function AdminAppointments() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', date: null });
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchAppointments();
  }, [user, navigate]);

  // PROMPT BLINDADO: Refetch quando filtros mudam
  useEffect(() => {
    if (user && user.user_type === 'admin') {
      fetchAppointments(true);
    }
  }, [filters.status, filters.date]);

  const fetchAppointments = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.date) params.append('date', format(filters.date, 'yyyy-MM-dd'));
      
      const res = await axios.get(`${API}/admin/dashboard-v2/appointments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(res.data.appointments || []);
      
      // Calcular estatísticas
      const allAppts = res.data.appointments || [];
      const statsCalc = {};
      Object.keys(STATUS_CONFIG).forEach(status => {
        statsCalc[status] = allAppts.filter(a => a.status === status).length;
      });
      setStats(statsCalc);
    } catch (error) {
      console.error(error);
      setAppointments([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const filteredAppointments = appointments.filter(appt => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        appt.client?.name?.toLowerCase().includes(search) ||
        appt.provider?.name?.toLowerCase().includes(search) ||
        appt.provider?.business_name?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    return true;
  });

  const clearFilters = () => setFilters({ search: '', status: '', date: null });
  const hasFilters = filters.search || filters.status || filters.date;

  if (loading) {
    return (
      <AdminLayout title="Agendamentos" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Agendamentos</h1>
            <p className="text-slate-500">Visualize e gerencie todos os agendamentos</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchAppointments(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <Card 
                key={key}
                className={`p-3 cursor-pointer transition-all ${filters.status === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setFilters({ ...filters, status: filters.status === key ? '' : key })}
              >
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded ${config.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{stats[key] || 0}</p>
                    <p className="text-xs text-slate-500">{config.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por cliente ou prestador..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[200px] justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {filters.date ? format(filters.date, 'dd/MM/yyyy') : 'Filtrar por data'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.date}
                  onSelect={(date) => setFilters({ ...filters, date })}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
            
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Appointments List */}
        <div className="space-y-3">
          {filteredAppointments.length > 0 ? (
            filteredAppointments.map((appt) => {
              const statusConfig = STATUS_CONFIG[appt.status] || STATUS_CONFIG.PENDING;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={appt.id} className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${statusConfig.color}`}>
                      <StatusIcon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold">{appt.client?.name || 'Cliente'}</p>
                        <span className="text-slate-400">→</span>
                        <p className="text-slate-600">{appt.provider?.business_name || appt.provider?.name || 'Prestador'}</p>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          {appt.date || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {appt.time || 'N/A'}
                        </span>
                        {appt.service_type && (
                          <span className="flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            {appt.service_type}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-xs text-slate-400">Criado em</p>
                      <p className="text-sm">{formatDate(appt.created_at)}</p>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhum agendamento encontrado</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'Os agendamentos aparecerão aqui'}
              </p>
            </Card>
          )}
        </div>

        {/* Summary */}
        {filteredAppointments.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {filteredAppointments.length} de {appointments.length} agendamentos
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
